import { useLayoutEffect, useRef, useState } from 'react';

type Cell = number | string | null;
type FxGrid = Cell[][];

export interface PlacedFx {
  col: number;
  row: number;
  value: number | string;
  /** How many dice of this value are now in the column (2 = pair, 3 = triple) */
  combo: number;
}

export interface DestroyedFx {
  col: number;
  row: number;
  value: number | string;
}

export interface MovedFx {
  col: number;
  fromRow: number;
  toRow: number;
}

export interface GridFx {
  id: number;
  placed: PlacedFx[];
  destroyed: DestroyedFx[];
  moved: MovedFx[];
  /** Column score lost to destruction (positive amount) */
  scoreDrops: { col: number; amount: number }[];
}

export interface BoardFx {
  player: GridFx | null;
  opponent: GridFx | null;
}

interface DiceEntry {
  row: number;
  value: number | string;
}

const FX_LIFETIME_MS = 1400;

const diceOf = (col: Cell[]): DiceEntry[] => {
  const entries: DiceEntry[] = [];
  col.forEach((value, row) => {
    if (value !== null && value !== undefined) entries.push({ row, value });
  });
  return entries;
};

const colScore = (col: Cell[]): number => {
  const counts: Record<number, number> = {};
  col.forEach((v) => {
    if (typeof v === 'number') counts[v] = (counts[v] || 0) + 1;
  });
  return Object.entries(counts).reduce((sum, [v, c]) => sum + Number(v) * c * c, 0);
};

const diffColumn = (
  colIdx: number,
  oldCol: Cell[],
  newCol: Cell[]
): { placed: PlacedFx[]; destroyed: DestroyedFx[]; moved: MovedFx[] } | null => {
  const oldD = diceOf(oldCol);
  const newD = diceOf(newCol);

  if (newD.length > oldD.length) {
    // Placement: existing dice must be an untouched prefix of the new dice
    const isPrefix = oldD.every((d, i) => newD[i].value === d.value && newD[i].row === d.row);
    if (!isPrefix) return null;
    const placed = newD.slice(oldD.length).map((d) => ({
      col: colIdx,
      row: d.row,
      value: d.value,
      combo: typeof d.value === 'number' ? newD.filter((x) => x.value === d.value).length : 1,
    }));
    return { placed, destroyed: [], moved: [] };
  }

  // Removal (or no change): surviving dice must be an in-order subsequence of the old dice
  const destroyed: DestroyedFx[] = [];
  const moved: MovedFx[] = [];
  let j = 0;
  for (const d of oldD) {
    if (j < newD.length && newD[j].value === d.value) {
      if (newD[j].row !== d.row) moved.push({ col: colIdx, fromRow: d.row, toRow: newD[j].row });
      j++;
    } else {
      destroyed.push({ col: colIdx, row: d.row, value: d.value });
    }
  }
  if (j !== newD.length) return null;
  return { placed: [], destroyed, moved };
};

const diffGrid = (oldGrid: FxGrid, newGrid: FxGrid): Omit<GridFx, 'id'> | null => {
  if (oldGrid === newGrid) return null;
  if (!oldGrid || !newGrid || oldGrid.length !== newGrid.length) return null;

  const placed: PlacedFx[] = [];
  const destroyed: DestroyedFx[] = [];
  const moved: MovedFx[] = [];
  const scoreDrops: GridFx['scoreDrops'] = [];

  for (let c = 0; c < newGrid.length; c++) {
    const d = diffColumn(c, oldGrid[c], newGrid[c]);
    // Unrecognized transition (reset / full state sync) -> skip effects entirely
    if (!d) return null;
    placed.push(...d.placed);
    destroyed.push(...d.destroyed);
    moved.push(...d.moved);
    if (d.destroyed.length > 0) {
      const drop = colScore(oldGrid[c]) - colScore(newGrid[c]);
      if (drop > 0) scoreDrops.push({ col: c, amount: drop });
    }
  }

  if (!placed.length && !destroyed.length && !moved.length) return null;
  return { placed, destroyed, moved, scoreDrops };
};

const isEmptyGrid = (g: FxGrid) => g.every((col) => col.every((c) => c === null));

/**
 * Watches both grids and emits animation events (placed / destroyed / moved dice)
 * by diffing consecutive states. Works for single-player and multiplayer alike,
 * since both feed the Game view plain grid props.
 */
export const useGridFx = (playerGrid: FxGrid, opponentGrid: FxGrid): BoardFx => {
  const prevRef = useRef<{ p: FxGrid; o: FxGrid } | null>(null);
  const idRef = useRef(0);
  const [fx, setFx] = useState<BoardFx>({ player: null, opponent: null });

  useLayoutEffect(() => {
    const prev = prevRef.current;
    prevRef.current = { p: playerGrid, o: opponentGrid };
    if (!prev) return;

    // Board reset (new game / rematch): clear any lingering effects
    if (isEmptyGrid(playerGrid) && isEmptyGrid(opponentGrid)) {
      setFx((f) => (f.player || f.opponent ? { player: null, opponent: null } : f));
      return;
    }

    const pFx = diffGrid(prev.p, playerGrid);
    const oFx = diffGrid(prev.o, opponentGrid);
    if (!pFx && !oFx) return;

    idRef.current += 1;
    const id = idRef.current;
    setFx({
      player: pFx ? { ...pFx, id } : null,
      opponent: oFx ? { ...oFx, id } : null,
    });

    const timer = setTimeout(() => {
      setFx((f) => (f.player?.id === id || f.opponent?.id === id ? { player: null, opponent: null } : f));
    }, FX_LIFETIME_MS);
    return () => clearTimeout(timer);
  }, [playerGrid, opponentGrid]);

  return fx;
};
